import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

const TOKEN = import.meta.env.VITE_MAPBOX_PUBLIC_TOKEN as string;

interface PublicLocationData {
  location: {
    id: string;
    name: string;
    businessName: string | null;
    address: string;
    lat: number;
    lng: number;
    notes: string | null;
    photosJson: string | null;
    siteContactName: string | null;
    siteContactPhone: string | null;
    siteContactEmail: string | null;
    createdAt: string;
  };
  supports: { id: string; name: string; address: string; lat: number; lng: number }[];
}

export function PublicLocationView() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<PublicLocationData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    fetch(`/api/public/locations/${token}`)
      .then(r => r.json())
      .then((res) => {
        const r = res as { success: boolean; data: PublicLocationData; error?: string };
        if (!r.success) { setError(r.error ?? 'Not found'); return; }
        setData(r.data);
      })
      .catch(() => setError('Failed to load location'))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-400 text-sm">Loading…</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-gray-600 font-medium">{error ?? 'Location not found'}</p>
          <p className="text-gray-400 text-sm mt-1">This link may have expired or be invalid.</p>
        </div>
      </div>
    );
  }

  const { location, supports } = data;
  const photos: string[] = (() => { try { return JSON.parse(location.photosJson ?? '[]'); } catch { return []; } })();
  const displayName = location.businessName || location.name;
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location.address)}`;
  const staticMapUrl = `https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/pin-s(${location.lng},${location.lat})/${location.lng},${location.lat},15/480x240@2x?access_token=${TOKEN}`;

  return (
    <div className="min-h-screen bg-white print:bg-white">
      <div className="max-w-2xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">{displayName}</h1>
          {location.businessName && location.businessName !== location.name && (
            <p className="text-sm text-gray-500 mt-0.5">{location.name}</p>
          )}
          <a
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-600 hover:underline mt-1 inline-block print:text-gray-700 print:no-underline"
          >
            📍 {location.address}
          </a>
        </div>

        {/* Map thumbnail */}
        <div className="mb-6 rounded-xl overflow-hidden border border-gray-200 shadow-sm">
          <img
            src={staticMapUrl}
            alt="Map"
            className="w-full object-cover"
            style={{ height: 200 }}
          />
        </div>

        {/* Site Contact */}
        {(location.siteContactName || location.siteContactPhone || location.siteContactEmail) && (
          <div className="mb-6 bg-gray-50 rounded-xl p-4 border border-gray-200">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">Site Contact</p>
            {location.siteContactName && (
              <p className="font-semibold text-gray-800">{location.siteContactName}</p>
            )}
            <div className="flex flex-wrap gap-x-4 mt-1">
              {location.siteContactPhone && (
                <a href={`tel:${location.siteContactPhone}`} className="text-sm text-blue-600 hover:underline print:text-gray-700">
                  📞 {location.siteContactPhone}
                </a>
              )}
              {location.siteContactEmail && (
                <a href={`mailto:${location.siteContactEmail}`} className="text-sm text-blue-600 hover:underline print:text-gray-700">
                  ✉️ {location.siteContactEmail}
                </a>
              )}
            </div>
          </div>
        )}

        {/* Support locations */}
        {supports.length > 0 && (
          <div className="mb-6">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">Support Locations</p>
            <div className="space-y-2">
              {supports.map(sup => (
                <div key={sup.id} className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-amber-400 mt-1 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800">{sup.name}</p>
                    <p className="text-xs text-gray-500">{sup.address}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Notes */}
        {location.notes && (
          <div className="mb-6">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">Notes</p>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{location.notes}</p>
          </div>
        )}

        {/* Photos */}
        {photos.length > 0 && (
          <div className="mb-6">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">Photos</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {photos.map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt=""
                  className="w-full aspect-square object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity print:cursor-default"
                  onClick={() => window.open(src, '_blank')}
                />
              ))}
            </div>
          </div>
        )}

        {/* Print button */}
        <div className="flex justify-center pt-2 print:hidden">
          <button
            onClick={() => window.print()}
            className="px-5 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors"
          >
            Print / Save as PDF
          </button>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center print:mt-4">
          <p className="text-xs text-gray-300">Powered by Location Breaker</p>
        </div>
      </div>
    </div>
  );
}
